import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Box,
  Snackbar,
  Card,
  CardContent,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  AccountBalance as LedgerIcon,
  Business as CompanyIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import Layout from "../components/layout/Layout";
import configuration from "../config";
import { setSelectedCompany } from "../features/counter/kycSlice.js";

const MotionCard = motion(Card);
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const LedgerSetup = () => {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      fname: "",
      lname: "",
      address1: "",
      eaddress: "",
      esubaddress: "",
    },
  });

  const [ledgers, setLedgers] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { API_BASE_URL } = configuration;
  const dispatch = useDispatch();
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
  const selectedCompanyName = useSelector(
    (state) => state.kyc.selectedCompanyName
  );

  useEffect(() => {
    if (selectedCompanyId) {
      fetchLedgerData();
      fetchCompanyInfo();
    } else {
      setLedgers([]);
    }
  }, [selectedCompanyId]);

  const fetchLedgerData = async () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/ledgerdata/${selectedCompanyId}`
      );
      
      if (!response.data || response.data.length === 0) {
        setLedgers([]);
        setSnackbar({
          open: true,
          message: "No ledger data found for this company.",
          severity: "info",
        });
      } else {
        setLedgers(response.data);
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      setLedgers([]);
      setSnackbar({
        open: true,
        message: "Failed to fetch ledger data. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/ledgerdata/${selectedCompanyId}`
      );
      dispatch(
        setSelectedCompany({
          id: response.data.id,
          name: response.data.name,
        })
      );
    } catch (error) {
      console.error("Error fetching company info:", error);
    }
  };

  const onSubmit = async (data) => {
    setFormLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/v1/ledger`, {
        ...data,
        kycId: selectedCompanyId,
      });
      setSnackbar({
        open: true,
        message: "Ledger created successfully ! 🎉",
        severity: "success",
      });
      reset();
      fetchLedgerData();
    } catch (error) {
       const errorMessage = 
        error.response?.data?.message ||
        "Failed to create ledger. The server might be down or the data is invalid."; 
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!selectedCompanyId) {
      setSnackbar({
        open: true,
        message: "Please select a company first",
        severity: "warning",
      });
      setAnchorEl(null);
      return;
    }

    setExcelLoading(true);
    setAnchorEl(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/LedgerDataExcel/${selectedCompanyId}?format=excel`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = `ledger_data_${new Date().toISOString().split("T")[0]}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "Excel file downloaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      setSnackbar({
        open: true,
        message: "Failed to download Excel file",
        severity: "error",
      });
    } finally {
      setExcelLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/deleteLedger/${id}`);
      setSnackbar({
        open: true,
        message: "Ledger deleted successfully",
        severity: "success",
      });
      setLedgers((prevLedgers) =>
        prevLedgers.filter((ledger) => ledger._id !== id)
      );
    } catch (error) {
      console.error("Error deleting ledger:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete ledger",
        severity: "error",
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2 },
    },
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <MotionBox
          sx={{ py: 4 }}
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <MotionPaper
            elevation={2}
            variants={itemVariants}
            sx={{
              p: 4,
              mb: 4,
              background: "linear-gradient(135deg, #EB5A3C 0%, #FF7A5C 100%)",
              color: "white",
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LedgerIcon sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h3" fontWeight="bold">
                    Ledger Management
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 1 }}>
                    {selectedCompanyName && (
                      <>
                        <CompanyIcon sx={{ mr: 1, fontSize: 20 }} />
                        <Chip
                          label={`Company: ${selectedCompanyName}`}
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.2)",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.1)",
                opacity: 0.3,
              }}
            />
          </MotionPaper>

          <MotionCard
            variants={itemVariants}
            sx={{ mb: 4, borderRadius: 3, overflow: "hidden" }}
          >
            <Box
              sx={{
                background: "linear-gradient(90deg, #F8EEDB 0%, #FFFAF0 100%)",
                p: 3,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <Typography variant="h5" fontWeight="bold" color="text.primary">
                Create New Ledger
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add a new ledger entry to your accounting system.
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <motion.div variants={itemVariants}>
                      <Controller
                        name="fname"
                        control={control}
                        rules={{ required: "Ledger Name is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Ledger Name"
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 4px 12px rgba(235, 90, 60, 0.15)",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </motion.div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <motion.div variants={itemVariants}>
                      <Controller
                        name="lname"
                        control={control}
                        rules={{ required: "Group is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Group"
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 4px 12px rgba(235, 90, 60, 0.15)",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </motion.div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <motion.div variants={itemVariants}>
                      <Controller
                        name="address1"
                        control={control}
                        rules={{ required: "Sub Group is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Sub Group"
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 4px 12px rgba(235, 90, 60, 0.15)",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </motion.div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <motion.div variants={itemVariants}>
                      <Controller
                        name="eaddress"
                        control={control}
                        rules={{ required: "Ledger is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Ledger"
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 4px 12px rgba(235, 90, 60, 0.15)",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </motion.div>
                  </Grid>

                  <Grid item xs={12}>
                    <motion.div variants={itemVariants}>
                      <Controller
                        name="esubaddress"
                        control={control}
                        rules={{ required: "Sub Ledger is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Sub Ledger"
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow:
                                    "0 4px 12px rgba(235, 90, 60, 0.15)",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </motion.div>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button onClick={() => reset()} variant="outlined" size="large" startIcon={<RefreshIcon />} sx={{ px: 3 }}>
                      Reset
                    </Button>
                  </motion.div>
                  
                  <Button
                    onClick={downloadExcel}
                    disabled={excelLoading}
                    variant="outlined"
                    startIcon={excelLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                    sx={{
                      borderColor: "#EB5A3C",
                      color: "#EB5A3C",
                      "&:hover": {
                        borderColor: "#C54428",
                        backgroundColor: "rgba(235, 90, 60, 0.1)",
                      },
                    }}
                  >
                    {excelLoading ? "Downloading..." : "Download Excel"}
                  </Button>
                  
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={formLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                      disabled={formLoading}
                      sx={{
                        px: 4,
                        background:
                          "linear-gradient(135deg, #EB5A3C 0%, #FF7A5C 100%)",
                        boxShadow: "0 4px 15px rgba(235, 90, 60, 0.4)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #C54428 0%, #EB5A3C 100%)",
                          boxShadow: "0 6px 20px rgba(235, 90, 60, 0.6)",
                        },
                      }}
                    >
                      {formLoading ? "Creating..." : "Create Ledger"}
                    </Button>
                  </motion.div>
                </Box>
              </form>
            </CardContent>
          </MotionCard>
          
          <MotionCard
            variants={itemVariants}
            sx={{ borderRadius: 3, overflow: "hidden" }}
          >
            <Box
              sx={{
                background: "linear-gradient(90deg, #F8EEDB 0%, #FFFAF0 100%)",
                p: 3,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  Existing Ledgers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Manage your current ledger entries ({ledgers.length} total)
                </Typography>
              </Box>
              <IconButton
                onClick={fetchLedgerData}
                disabled={loading}
                sx={{
                  backgroundColor: "rgba(235, 90, 60, 0.1)",
                  "&:hover": { backgroundColor: "rgba(235, 90, 60, 0.2)" },
                }}
              >
                <RefreshIcon sx={{ color: "#EB5A3C" }} />
              </IconButton>
            </Box>

            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                  <CircularProgress />
                </Box>
              ) : ledgers.length > 0 ? (
                <AnimatePresence mode="popLayout">
                  {ledgers.map((ledger, index) => (
                    <motion.div
                      key={ledger._id}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      layout
                      transition={{ delay: index * 0.05 }}
                    >
                      <Box
                        sx={{
                          p: 3,
                          borderBottom:
                            index < ledgers.length - 1
                              ? "1px solid rgba(0,0,0,0.08)"
                              : "none",
                          position: "relative",
                          transition: "background-color 0.3s ease",
                          "&:hover": {
                            backgroundColor: "#F8F9FA",
                            "& .delete-button": {
                              opacity: 1,
                              transform: "translateX(0)",
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                color="primary.main"
                              >
                                {ledger.LegerName}
                              </Typography>
                              <Chip
                                label={ledger.group}
                                size="small"
                                sx={{
                                  ml: 2,
                                  backgroundColor: "rgba(235, 90, 60, 0.1)",
                                  color: "#EB5A3C",
                                  fontWeight: "bold",
                                }}
                              />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Sub Group
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {ledger.subGroup}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Ledger Category
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {ledger.ledgerCategory}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Sub Ledger Category
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {ledger.subLedgerCategory}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>

                          <Box
                            className="delete-button"
                            sx={{
                              opacity: 0.7,
                              transform: "translateX(0)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                opacity: 1,
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <IconButton
                              onClick={() => handleDelete(ledger._id)}
                              color="error"
                              sx={{
                                backgroundColor: "rgba(231, 76, 60, 0.1)",
                                "&:hover": {
                                  backgroundColor: "rgba(231, 76, 60, 0.2)",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </motion.div>
                  ))}
                </AnimatePresence>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <LedgerIcon sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No ledgers available
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Create your first ledger using the form above.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </MotionBox>
      </Container>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default LedgerSetup;
